import { Box, Flex, Text } from "@chakra-ui/react";
import { default as BaseSelect, SelectorProps } from "components/Select";
import {
  DateAdditionalInfo,
  DateFromPhotoOptions,
  DATE_FORMAT_OPTIONS,
  DEFAULT_DATE_FROM_PHOTO_ID,
  DEFAULT_DATE_FROM_PHOTO_LABEL,
  InspectionItemResult,
} from "interfaces/models/inspectionItemResult";
import { isUndefined } from "lodash-es";
import { useMemo } from "react";
import Error from "../../../builder/groups/GroupTable/error";
import { useFormBuilder } from "../../../context/FormBuilderContext/context";
import withField from "../../../hooks/withField";
import { DATE_UPDATE_TRIGGER_DATA_TYPES } from "../../../setup/define";
import { FieldEditorProps } from "../../types";

const Select = withField<SelectorProps>(BaseSelect, (e) => e);

interface DateEditorProps extends FieldEditorProps {}

const DateEditor: React.FC<DateEditorProps> = (props) => {
  const { groupRowIndex, inspectionResult, onChange } = props;
  const additionInfoDate = inspectionResult?.additionInfo as DateAdditionalInfo;
  const isInvalid = isUndefined(additionInfoDate?.formatType);

  const { state } = useFormBuilder();
  const itemResults = state.itemResults;
  const mapRowOrderInfo = state.mapRowOrderInfo;

  const dateAdditionalOptions = useMemo(() => {
    const fields: DateFromPhotoOptions[] = [
      {
        id: DEFAULT_DATE_FROM_PHOTO_ID,
        name: DEFAULT_DATE_FROM_PHOTO_LABEL,
        index: 0,
        total: 0,
        isDefault: true,
      },
    ];

    itemResults.forEach((item) => {
      if (
        !mapRowOrderInfo?.[item.id] ||
        !DATE_UPDATE_TRIGGER_DATA_TYPES.includes(item.dataType)
      ) {
        return;
      }

      fields.push({
        id: item.id,
        ...mapRowOrderInfo?.[item.id],
        name: item.name,
      });
    });

    return fields
      .sort((a, b) => a.index - b.index)
      .map((item) => ({
        value: item.id,
        label: (
          <Flex alignItems="center" gap="0.4rem">
            {!item?.isDefault && (
              <Text letterSpacing="2px" fontSize="1.2rem">
                <Text as="b" fontSize="1.6rem">
                  {item.index}
                </Text>
                /{`${item.total}:`}
              </Text>
            )}
            <Text fontSize="1.4rem">{item.name}</Text>
          </Flex>
        ),
      }));
  }, [itemResults, mapRowOrderInfo]);

  const onChangeAdditionInfo = (info: Partial<DateAdditionalInfo>) => {
    const data = {
      additionInfo: {
        ...inspectionResult.additionInfo,
        ...info,
      },
    } satisfies Partial<InspectionItemResult>;

    onChange(data);
  };

  return (
    <Box mt="1.5rem" minWidth="70rem">
      <Flex gap="1rem">
        <Box flexBasis="30rem">
          <Text fontSize="1.4rem" mb="0.5rem" fontWeight="bold">
            フォーマット
          </Text>
          <Select
            isInvalid={isInvalid}
            path={[
              "itemResults",
              groupRowIndex,
              "additionInfo",
              "date",
              "formatType",
            ]}
            display="inline-block"
            hideSearch
            backgroundColor={"#fff"}
            borderColor="#eeeeee"
            borderRadius="0.5rem"
            width="100%"
            maxWidth="30rem"
            useDebounce={false}
            options={DATE_FORMAT_OPTIONS}
            value={additionInfoDate?.formatType}
            onChange={(formatType) => {
              onChangeAdditionInfo({ formatType });
            }}
          />
        </Box>

        <Box flexBasis="30rem">
          <Text fontSize="1.4rem" mb="0.5rem" fontWeight="bold">
            デフォルト値の取得元
          </Text>
          <BaseSelect
            hideSearch
            backgroundColor="#fff"
            borderColor="#eeeeee"
            borderRadius="0.5rem"
            width="100%"
            maxWidth="30rem"
            listHeight={178}
            options={dateAdditionalOptions}
            value={
              additionInfoDate?.dateFromPhotoId ?? DEFAULT_DATE_FROM_PHOTO_ID
            }
            onChange={(dateFromPhotoId) => {
              onChangeAdditionInfo({ dateFromPhotoId });
            }}
          />
        </Box>
      </Flex>
      {isInvalid && <Error pl="0px" />}
    </Box>
  );
};

export default DateEditor;
